import * as CL from '@design-system/component-library';
import * as React from 'react';
import { Icon } from '../Icon/Icon';
import { Price, getSaving, isMobileView } from './SubscriptionUtils';
import { SubscriptionModal } from './SubscriptionModal';
import { closeMsg, contractPriceCapitalizedMsg, t } from '../../common/i18n';
import { createRef, useRef, useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses';
import { sendSelectionAnalyticsEvent } from '../SubscriptionCards/subscriptionCardsAnalyticsUtils';
import { useAuth } from '../../public/site/AuthProvider';
import type { CLPopoverImperativeRefHandle } from '../../common/types/componentLibrary';
import type { MobileBroadbandSubCardProps } from '../SubscriptionCard/SubscriptionCard';
import type { ModalDescriptionItem } from './SubscriptionModal';
import type { OrderProps, SalesProductsOrderProps, SubscriptionCardWithModel } from './types';
import type { SubCardWithAnalyticsV4Props } from '../SubscriptionCards/subscriptionCardsAnalyticsUtils';

import './SubscriptionCard.scss';

export interface SubscriptionCardProps {
  modal?: JSX.Element;
  badges: JSX.Element[];
  subscriptionCard?: SubscriptionCardWithModel;
  subHeader: string;
  header: string;
  description: string;
  infoItems: InfoItem[];
  hasQuantity: boolean;
  buttonText: string;
  onOrder: (p: SalesProductsOrderProps | OrderProps) => void;
  additionalFeatures?: InfoItem[];
  modalDescriptionItems?: ModalDescriptionItem[];
  publicPages?: boolean;
  listName?: string;
  analyticsIndex: number;
  isChangeOffer: boolean;
  buttonMakesOrder?: boolean;
}

export interface InfoItem {
  icon: string;
  text: string;
  popoverContent?: JSX.Element;
}

interface PopOverProps {
  children: React.ReactNode;
  popoverRef: React.RefObject<CLPopoverImperativeRefHandle>;
}

export const SubscriptionCard = ({
  badges,
  subHeader,
  header,
  description,
  infoItems,
  hasQuantity,
  buttonText,
  additionalFeatures,
  subscriptionCard,
  modalDescriptionItems,
  onOrder,
  publicPages,
  listName,
  analyticsIndex,
  isChangeOffer,
  buttonMakesOrder,
}: SubscriptionCardProps) => {
  const [showModal, setShowModal] = React.useState(false);
  const onlineModel = subscriptionCard?.onlineModel;
  const { isAuthenticated } = useAuth();
  const [quantity, setQuantity] = useState<number>(1);
  const [newOrKeepNumber, setNewOrKeepNumber] = useState('newNumber');

  const activePopoverRef = useRef<CLPopoverImperativeRefHandle | null>(null);
  const infoItemRefs = useRef<React.RefObject<CLPopoverImperativeRefHandle>[]>(
    infoItems.map(() => createRef<CLPopoverImperativeRefHandle>())
  );
  const additionalFeatureRefs = useRef<React.RefObject<CLPopoverImperativeRefHandle>[]>(
    additionalFeatures ? additionalFeatures.map(() => createRef<CLPopoverImperativeRefHandle>()) : []
  );

  const getVoucherBadge = () => {
    if (subscriptionCard?.voucherCode) {
      return <CL.Badge color="discount" text={subscriptionCard.voucherCode} />;
    }
    return undefined;
  };

  const getContractPriceBadge = () => {
    if (
      !subscriptionCard?.voucherCode &&
      (subscriptionCard?.originalMonthlyPrice || 0 > 0 || subscriptionCard?.originalOneTimePrice)
    ) {
      return <CL.Badge color="discount" text={t.JPKP(contractPriceCapitalizedMsg)} />;
    }
    return undefined;
  };

  const allBadges = [...badges, ...[getVoucherBadge()], ...[getContractPriceBadge()]];

  const PopOver: React.FC<PopOverProps> = ({ children, popoverRef }) => (
    <CL.Popover
      ref={popoverRef}
      triggerElement={<Icon color="blue-600" icon="information" size="m" type="regular" />}
      placement="right"
      defaultOpen={false}
      i18n_popover_closeButtonTitle={t.WOYD(closeMsg)}
      onOpen={() => {
        if (activePopoverRef.current && activePopoverRef.current !== popoverRef.current) {
          activePopoverRef.current.closePopover();
        }
        activePopoverRef.current = popoverRef.current;
      }}
      onClose={() => {
        activePopoverRef.current = null;
      }}
    >
      {children}
    </CL.Popover>
  );

  const InfoItemsList = () => (
    <ul className={`${dsClass.MARGIN_BOTTOM_0}`}>
      {infoItems.map((infoItem, index) => (
        <li key={infoItem.text}>
          <CL.Icon className="of-info-icon" icon={infoItem.icon} size="s" />
          <span className={`of-info-item-text ${dsClass.TEXT_S}`}>{infoItem.text}</span>
          {infoItem.popoverContent && (
            <PopOver popoverRef={infoItemRefs.current[index]}>{infoItem.popoverContent}</PopOver>
          )}
        </li>
      ))}
    </ul>
  );

  const AdditionalFeaturesList = () => (
    <ul className={`of-two-column-flex ${dsClass.MARGIN_TOP_2}`}>
      {additionalFeatures?.map((feature, index) => (
        <li key={feature.text} className={`${dsClass.PADDING_BOTTOM_1}`}>
          <CL.Icon className="of-info-icon" size="s" icon={feature.icon} />
          <span className={`of-info-item-text ${dsClass.TEXT_S}`}>{feature.text}</span>
          {feature.popoverContent && (
            <PopOver popoverRef={additionalFeatureRefs.current[index]}>{feature.popoverContent}</PopOver>
          )}
        </li>
      ))}
    </ul>
  );

  const Modal = () =>
    onlineModel ? (
      <SubscriptionModal
        header={header}
        onOrder={onOrder}
        descriptionItems={modalDescriptionItems || []}
        offers={(subscriptionCard as SubCardWithAnalyticsV4Props).offers}
        newOrKeepNumber={newOrKeepNumber}
        onNumberSelectionChange={(n: string) => setNewOrKeepNumber(n)}
        onCloseDialog={() => {
          setShowModal(false);
        }}
        onlineModel={onlineModel}
        subscriptionCard={subscriptionCard}
        publicPages={publicPages}
        hasQuantity={!isChangeOffer}
        isChangeOffer={isChangeOffer}
      />
    ) : null;

  const Accordion = () => (
    <div className="of-subscription-card-content-additional-features">
      {additionalFeatures && (
        <CL.Accordion className={`${dsClass.MARGIN_TOP_2}`}>
          <CL.AccordionItem
            heading={
              <h5 className={dsClass.MARGIN_0}>{`${t.Y2TH('Additional features')} (${additionalFeatures.length})`}</h5>
            }
          >
            <AdditionalFeaturesList />
          </CL.AccordionItem>
        </CL.Accordion>
      )}
    </div>
  );

  return (
    <>
      {showModal ? <Modal /> : null}
      <div className="of-subscription-card-container">
        <div className={`of-subscription-card-content ${dsClass.BORDER_WIDTH_1} ${dsClass.BORDER_STYLE_SOLID}`}>
          <div className="of-subscription-card-content-badges">{allBadges}</div>
          <div className={`of-subscription-card-content-flex-container ${dsClass.PADDING_BOTTOM_2}`}>
            <div className="of-subscription-card-content-flex-item of-main-content">
              <div className="of-subscription-card-content-flex-container">
                <div className="of-subscription-card-content-flex-item of-main-text-content">
                  <div className={`of-subscription-card-content-header ${dsClass.MARGIN_TOP_3}`}>
                    <span>{subHeader}</span>
                    <h3 className={`${dsClass.MARGIN_TOP_1} ${dsClass.MARGIN_BOTTOM_2}`}>{header}</h3>
                  </div>
                  <div className="of-subscription-card-content-description">
                    <span className={dsClass.TEXT_M}>{description}</span>
                  </div>
                </div>
                <div className="of-subscription-card-content-flex-item of-main-list-content of-subscription-card-align-content-end">
                  <div className="of-subscription-card-content-info-list">
                    <InfoItemsList />
                  </div>
                </div>
              </div>
              {isMobileView && <Accordion />}
            </div>

            <div className="of-subscription-card-content-flex-item of-price-content">
              <div className={`of-subscription-card-content-price ${dsClass.MARGIN_BOTTOM_1}`}>
                <Price
                  onetime={isChangeOffer ? subscriptionCard?.serviceFee : subscriptionCard?.oneTimePrice}
                  monthly={subscriptionCard?.monthlyPrice}
                  inModal={false}
                  quantity={quantity}
                  saving={getSaving(subscriptionCard, quantity)}
                  isChangeOffer={isChangeOffer}
                />
              </div>
              <div className="of-subscription-card-content-button">
                {hasQuantity && (
                  <CL.Quantity
                    className={`of-subscription-quantity ${dsClass.MARGIN_RIGHT_2}`}
                    minValue={1}
                    currentValue={1}
                    onChange={setQuantity}
                  />
                )}
                <CL.Button
                  size="l"
                  onClick={() => {
                    buttonMakesOrder && onlineModel
                      ? onOrder({
                          isAuthenticated: isAuthenticated,
                          onlineModel: onlineModel,
                          quantity: quantity,
                          isNewNumber: true,
                          noeOfferCode: (subscriptionCard as SubCardWithAnalyticsV4Props).selectedOffer,
                          voucherCode: subscriptionCard?.voucherCode,
                          salesProductOfferCode: (subscriptionCard as MobileBroadbandSubCardProps).salesProduct
                            .offerCode,
                          services: subscriptionCard?.services,
                        })
                      : sendSelectionAnalyticsEvent(
                          subscriptionCard as SubCardWithAnalyticsV4Props,
                          analyticsIndex,
                          listName
                        );
                    if (!buttonMakesOrder) {
                      setShowModal(true);
                    }
                  }}
                >
                  {buttonText}
                </CL.Button>
              </div>
            </div>
          </div>
          {!isMobileView && <Accordion />}
        </div>
      </div>
    </>
  );
};
